import { useCallback, useEffect, useRef } from "react";

export type Point = { x: number; y: number };

/** How long to keep the current flyout open while the cursor seems to aim at it. */
const ACTIVATION_DELAY_MS = 200;
/** Vertical px the aim cone is extended past the panel to forgive imprecise travel. */
const EDGE_TOLERANCE_PX = 75;
/** Recent cursor samples kept for trajectory comparison. */
const TRACKED_LOCS = 3;

const slope = (from: Point, to: Point) => (to.y - from.y) / (to.x - from.x);

/**
 * True when the cursor's trajectory is converging on the open panel — the user is
 * plausibly steering toward it diagonally, so a sibling swap should wait.
 *
 * Flyout panels open to the right of the trigger column (`left: 50%`), so the far
 * edge is `panel.right`. The safe cone runs from the cursor to that edge's two
 * corners; if the cone widens around the direction of travel, the cursor is heading
 * inside it. (menu-aim algorithm, Ben Kamens.)
 */
export function isAimingAtPanel(
  prevLoc: Point,
  loc: Point,
  panelRect: { top: number; bottom: number; right: number }
): boolean {
  // Cursor already past the panel's far edge can't be crossing the triggers.
  if (loc.x >= panelRect.right) {
    return false;
  }

  const topCorner: Point = {
    x: panelRect.right,
    y: panelRect.top - EDGE_TOLERANCE_PX,
  };
  const bottomCorner: Point = {
    x: panelRect.right,
    y: panelRect.bottom + EDGE_TOLERANCE_PX,
  };

  return (
    slope(loc, topCorner) < slope(prevLoc, topCorner) &&
    slope(loc, bottomCorner) > slope(prevLoc, bottomCorner)
  );
}

export type UseFlyoutSafeAreaOptions = {
  /** Desktop + fine-pointer only; disabled interaction makes every handler a no-op. */
  enabled: boolean;
  /** Key of the currently open flyout, or null. */
  getOpenKey: () => string | null;
  /** Live bounding rect of the open panel, or null if none is open/measurable. */
  getOpenPanelRect: () => DOMRect | null;
  /** Commit a flyout change: a key to open, or null to close. */
  onActivate: (key: string | null) => void;
};

/**
 * Menu-aim guard for the nested flyout. Instead of swapping the open panel the
 * instant the cursor touches another trigger row, it defers the swap while the
 * cursor appears to be traveling diagonally toward the open panel, and cancels the
 * swap entirely once the cursor reaches that panel.
 */
export function useFlyoutSafeArea({
  enabled,
  getOpenKey,
  getOpenPanelRect,
  onActivate,
}: UseFlyoutSafeAreaOptions) {
  const locs = useRef<Point[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Position at which we last chose to defer; lets a parked cursor resolve.
  const lastDelayLoc = useRef<Point | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const trackMouseMove = useCallback(
    (event: { clientX: number; clientY: number }) => {
      if (!enabled) {
        return;
      }
      locs.current.push({ x: event.clientX, y: event.clientY });
      if (locs.current.length > TRACKED_LOCS) {
        locs.current.shift();
      }
    },
    [enabled]
  );

  const requestActivate = useCallback(
    (target: string | null) => {
      if (!enabled) {
        return;
      }
      clearPending();
      lastDelayLoc.current = null;

      const attempt = () => {
        const openKey = getOpenKey();
        if (target === openKey) {
          // Already in the desired state — nothing to swap.
          return;
        }

        const panelRect = openKey === null ? null : getOpenPanelRect();
        const loc = locs.current[locs.current.length - 1];
        const prevLoc = locs.current[locs.current.length - 2];

        // Nothing open, or not enough history/geometry to judge aim → swap now.
        if (!panelRect || !loc || !prevLoc) {
          onActivate(target);
          return;
        }

        // Cursor reached the open panel → abandon the deferred swap entirely.
        const insidePanel =
          loc.x >= panelRect.left &&
          loc.x <= panelRect.right &&
          loc.y >= panelRect.top &&
          loc.y <= panelRect.bottom;
        if (insidePanel) {
          return;
        }

        // Cursor parked since we last deferred → honor the hover and swap.
        if (
          lastDelayLoc.current &&
          loc.x === lastDelayLoc.current.x &&
          loc.y === lastDelayLoc.current.y
        ) {
          lastDelayLoc.current = null;
          onActivate(target);
          return;
        }

        if (isAimingAtPanel(prevLoc, loc, panelRect)) {
          lastDelayLoc.current = loc;
          timeoutRef.current = setTimeout(attempt, ACTIVATION_DELAY_MS);
          return;
        }

        lastDelayLoc.current = null;
        onActivate(target);
      };

      attempt();
    },
    [enabled, clearPending, getOpenKey, getOpenPanelRect, onActivate]
  );

  const cancelAndClose = useCallback(() => {
    if (!enabled) {
      return;
    }
    clearPending();
    lastDelayLoc.current = null;
    onActivate(null);
  }, [enabled, clearPending, onActivate]);

  useEffect(() => clearPending, [clearPending]);

  return { trackMouseMove, requestActivate, cancelAndClose };
}
